import { WEBSITE_MEDIA_LIMITS } from '@dukanti/shared';

/**
 * D-P0-7: a cover image from the video itself, made in the Admin browser
 * (seek a frame, draw it, encode it). There is no server transcoding. The
 * frame is taken from the local `File`, never from a storage URL. When this
 * browser cannot decode the video the merchant uploads a cover instead.
 *
 * The cover is WebP where the browser can encode it, else JPEG/PNG. It is
 * always labelled with the type the browser actually produced: Safari
 * (WebKit) cannot encode WebP and silently returns PNG from `toBlob`, so a
 * hard-coded WebP label would be refused by the byte checks before upload.
 */
export const POSTER_MAX_WIDTH = 1280;
export const POSTER_CAPTURE_TIMEOUT_MS = 8000;
const POSTER_QUALITY = 0.85;

export class PosterCaptureError extends Error {
  constructor(readonly reason: 'decode' | 'timeout' | 'encode') {
    super(reason);
    this.name = 'PosterCaptureError';
  }
}

export interface PosterCaptureDeps {
  readonly createVideo: () => HTMLVideoElement;
  readonly createImage: () => HTMLImageElement;
  readonly createCanvas: () => HTMLCanvasElement;
  readonly createObjectURL: (blob: Blob) => string;
  readonly revokeObjectURL: (url: string) => void;
  readonly timeoutMs: number;
}

export const defaultPosterCaptureDeps: PosterCaptureDeps = {
  createVideo: () => document.createElement('video'),
  createImage: () => new Image(),
  createCanvas: () => document.createElement('canvas'),
  createObjectURL: (blob) => URL.createObjectURL(blob),
  revokeObjectURL: (url) => URL.revokeObjectURL(url),
  timeoutMs: POSTER_CAPTURE_TIMEOUT_MS,
};

function once(
  video: HTMLVideoElement,
  success: 'loadeddata' | 'seeked',
  timeoutMs: number,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(
      () => finish(() => reject(new PosterCaptureError('timeout'))),
      timeoutMs,
    );
    const onSuccess = (): void => finish(resolve);
    const onError = (): void => finish(() => reject(new PosterCaptureError('decode')));
    function finish(run: () => void): void {
      clearTimeout(timer);
      video.removeEventListener(success, onSuccess);
      video.removeEventListener('error', onError);
      run();
    }
    video.addEventListener(success, onSuccess);
    video.addEventListener('error', onError);
  });
}

function draw(
  canvas: HTMLCanvasElement,
  source: HTMLVideoElement | HTMLImageElement,
  width: number,
  height: number,
): void {
  const ratio = Math.min(1, POSTER_MAX_WIDTH / width);
  canvas.width = Math.max(1, Math.round(width * ratio));
  canvas.height = Math.max(1, Math.round(height * ratio));
  const context = canvas.getContext('2d');
  if (!context) throw new PosterCaptureError('encode');
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
}

type PosterMime = 'image/webp' | 'image/jpeg' | 'image/png';

const POSTER_EXTENSIONS: Readonly<Record<PosterMime, string>> = {
  'image/webp': 'webp',
  'image/jpeg': 'jpg',
  'image/png': 'png',
};

/** Video frames have no alpha: JPEG before PNG keeps a portrait frame under the cap. */
export const VIDEO_POSTER_TYPES: readonly PosterMime[] = ['image/webp', 'image/jpeg', 'image/png'];

/** A GIF frame may be transparent: PNG before JPEG keeps it (JPEG would paint it black). */
export const GIF_POSTER_TYPES: readonly PosterMime[] = ['image/webp', 'image/png', 'image/jpeg'];

/** One encode attempt. Resolves `null` on a null blob or a synchronous throw; never rejects. */
function encodeOnce(canvas: HTMLCanvasElement, type: PosterMime): Promise<Blob | null> {
  return new Promise<Blob | null>((resolve) => {
    try {
      canvas.toBlob((blob) => resolve(blob), type, POSTER_QUALITY);
    } catch {
      // e.g. a SecurityError on a tainted canvas: treat as "this type failed".
      resolve(null);
    }
  });
}

/**
 * The first candidate type the browser really encodes, within the image size
 * cap, as a `File` labelled with that type; else `PosterCaptureError('encode')`.
 */
async function encodePoster(
  canvas: HTMLCanvasElement,
  types: readonly PosterMime[],
): Promise<File> {
  for (const type of types) {
    const blob = await encodeOnce(canvas, type);
    // The engine fell back (WebKit: WebP -> PNG) or answered nothing: try the next type.
    if (!blob || blob.type.trim().toLowerCase() !== type) continue;
    if (blob.size === 0 || blob.size > WEBSITE_MEDIA_LIMITS.image.maxBytes) continue;
    return new File([blob], `poster.${POSTER_EXTENSIONS[type]}`, { type });
  }
  throw new PosterCaptureError('encode');
}

/**
 * A `File` with a frame of the video (WebP where the browser can encode it,
 * else JPEG/PNG, labelled with the type actually produced), or a
 * `PosterCaptureError`.
 */
export async function captureVideoPoster(
  file: File,
  deps: PosterCaptureDeps = defaultPosterCaptureDeps,
): Promise<File> {
  const url = deps.createObjectURL(file);
  const video = deps.createVideo();
  try {
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    const loaded = once(video, 'loadeddata', deps.timeoutMs);
    video.src = url;
    await loaded;

    if (!(video.videoWidth > 0) || !(video.videoHeight > 0)) {
      throw new PosterCaptureError('decode');
    }

    // Half a second in (or the middle of a shorter clip): past a black first frame.
    const seek =
      Number.isFinite(video.duration) && video.duration > 0 ? Math.min(0.5, video.duration / 2) : 0;
    if (seek > 0) {
      const seeked = once(video, 'seeked', deps.timeoutMs);
      video.currentTime = seek;
      await seeked;
    }

    const canvas = deps.createCanvas();
    draw(canvas, video, video.videoWidth, video.videoHeight);
    return await encodePoster(canvas, VIDEO_POSTER_TYPES);
  } finally {
    video.removeAttribute('src');
    deps.revokeObjectURL(url);
  }
}

/**
 * The first frame of an animated GIF, as WebP where the browser can encode it,
 * else PNG/JPEG (the type the browser actually produced).
 */
export async function captureGifPoster(
  file: File,
  deps: PosterCaptureDeps = defaultPosterCaptureDeps,
): Promise<File> {
  const url = deps.createObjectURL(file);
  const image = deps.createImage();
  try {
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => reject(new PosterCaptureError('timeout')), deps.timeoutMs);
      image.onload = (): void => {
        clearTimeout(timer);
        resolve();
      };
      image.onerror = (): void => {
        clearTimeout(timer);
        reject(new PosterCaptureError('decode'));
      };
      image.src = url;
    });
    if (!(image.naturalWidth > 0) || !(image.naturalHeight > 0)) {
      throw new PosterCaptureError('decode');
    }
    const canvas = deps.createCanvas();
    draw(canvas, image, image.naturalWidth, image.naturalHeight);
    return await encodePoster(canvas, GIF_POSTER_TYPES);
  } finally {
    image.removeAttribute('src');
    deps.revokeObjectURL(url);
  }
}

/** A cover image for a video or an animated GIF, from the file itself. */
export function captureMediaPoster(
  file: File,
  deps: PosterCaptureDeps = defaultPosterCaptureDeps,
): Promise<File> {
  return file.type.trim().toLowerCase() === 'image/gif'
    ? captureGifPoster(file, deps)
    : captureVideoPoster(file, deps);
}
